/**
 * S3UploadManagerContext.tsx
 * 
 * Context for providing UploadJobManager instance across components
 * This bridges the gap between S3UploadContext and the UploadJobManager
 */

import React, { createContext, useContext, type ReactNode } from 'react';
import type { UploadJobManager } from '../services/uploadJobManager';

// ============================================================================
// Context Definition
// ============================================================================

/**
 * Context value interface
 */
export interface S3UploadManagerContextType {
  manager: UploadJobManager | null;
}

const S3UploadManagerContext = createContext<S3UploadManagerContextType | null>(null);

/**
 * Custom hook to use the upload manager context
 * Returns null manager if not within provider (allows graceful degradation)
 */
export const useS3UploadManager = (): S3UploadManagerContextType => {
  const context = useContext(S3UploadManagerContext);
  // Return null manager if not within provider (graceful degradation)
  return context ?? { manager: null };
};

/**
 * Props for S3UploadManagerProvider
 */
export interface S3UploadManagerProviderProps {
  children: ReactNode;
  manager: UploadJobManager | null;
}

/**
 * S3UploadManagerProvider component
 * 
 * Provides the UploadJobManager instance to child components
 */
export const S3UploadManagerProvider: React.FC<S3UploadManagerProviderProps> = ({
  children,
  manager,
}) => {
  return (
    <S3UploadManagerContext.Provider value={{ manager }}>
      {children}
    </S3UploadManagerContext.Provider>
  );
};

export default S3UploadManagerContext;
